"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  MemberPhotoCarousel,
  renderBooking,
} from "@openings/template-components";
import { formatPhoneNumberForDisplay } from "@utils/core";
import Avatar from "antd/es/avatar";

import {
  BookingProvider,
  useBookingContext,
} from "@/app/components/Booking/BookingContext";
import type {
  BookingInfoBlog,
  BookingInfoSchedule,
} from "@/app/components/BookingPage/BookingInfoPanel";
import { BookingPageHeaderWithDrawer } from "@/app/components/BookingPage/BookingPageHeaderWithDrawer";
import { BookingPageWrapper } from "@/app/components/BookingPage/BookingPageWrapper";
import { EditorialBookingPageRoot } from "@/app/components/BookingPage/EditorialBookingPageRoot";
import { EditorialServiceLedger } from "@/app/components/BookingPage/EditorialServiceLedger";
import type { BookingVisualVariantKey } from "@/app/components/BookingPage/bookingVisualVariants";
import { standaloneCardTitleStyle } from "@/app/components/BookingPage/editorialBookingStyles";
import type { MemberBookingData } from "@/app/components/Booking/bookingData";

type MemberPageClientProps = {
  salon: {
    id?: string;
    name: string;
    logoUrl?: string;
    phoneNumber?: string | null;
    supportEmail?: string | null;
    instagramHandle?: string | null;
    schedules?: BookingInfoSchedule[];
    blog?: BookingInfoBlog | null;
  };
  member: {
    id: string;
    name: string;
    photo: string | null;
    bio: string | null;
    /**
     * Set only when the teammate opted in to a public phone
     * (`TeamMember.phonePublic`, Issue #812). Their number is otherwise
     * private (Issue #774); the header drawer shows the business phone.
     */
    phone: string | null;
    instagramHandle: string | null;
    video?: { url: string; thumbnail?: string } | null;
    photos: Array<{
      id: string;
      src: string;
      alt?: string;
      type?: "image" | "video";
      thumbnail?: string;
      variants?: unknown;
    }>;
  };
  services?: MemberBookingData["services"];
  bookingData?: MemberBookingData;
  visualVariant?: BookingVisualVariantKey;
  homeHref?: string;
};

function MemberBookingSection() {
  const { booking } = useBookingContext();
  if (!booking) return null;
  return <>{renderBooking(booking)}</>;
}

function getInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

export function MemberPageClient({
  salon,
  member,
  services: standaloneServices,
  bookingData,
  visualVariant,
  homeHref,
}: MemberPageClientProps) {
  const [showFullBio, setShowFullBio] = useState(false);
  const [bioCanExpand, setBioCanExpand] = useState(false);
  const bioRef = useRef<HTMLParagraphElement>(null);

  const instagram = member.instagramHandle?.replace(/^@/, "") ?? null;
  const services = standaloneServices ?? bookingData?.services ?? [];

  const portfolioItems = useMemo(() => {
    const photos = [...member.photos];
    if (
      member.video?.url &&
      !photos.some((photo) => photo.src === member.video?.url)
    ) {
      photos.unshift({
        id: `${member.id}-profile-video`,
        src: member.video.url,
        alt: `${member.name} video`,
        type: "video",
        thumbnail: member.video.thumbnail,
      });
    }
    return photos;
  }, [member.id, member.name, member.photos, member.video]);

  useEffect(() => {
    if (!member.bio || showFullBio) return;
    const element = bioRef.current;
    if (!element) return;

    const measure = () => {
      setBioCanExpand(element.scrollHeight > element.clientHeight + 1);
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [member.bio, showFullBio]);

  const hasPortfolio = portfolioItems.length > 0;
  const hasServices = services.length > 0;
  const layoutVariant =
    hasPortfolio && hasServices
      ? ""
      : hasPortfolio
        ? " openings-member-detail-layout--no-services"
        : hasServices
          ? " openings-member-detail-layout--no-portfolio"
          : " openings-member-detail-layout--profile-only";

  const serviceGroups = [
    {
      key: "member-services",
      name: null,
      services: services.map((service) => ({
        id: service.id,
        title: service.title,
        description: service.description || undefined,
        price: service.price,
        up: service.up,
        duration: service.duration,
      })),
    },
  ];

  return (
    <EditorialBookingPageRoot
      className="openings-member-detail-theme"
      visualVariant={visualVariant}
    >
      <div className="openings-booking-page openings-member-detail-page">
        <style>{`
          .openings-booking-wrapper.openings-member-detail-header-wrapper {
            padding-top:
              var(--openings-booking-editorial-outer-padding, 32px);
          }
          .openings-booking-wrapper.openings-member-detail-body-wrapper {
            padding-bottom:
              var(--openings-booking-editorial-outer-padding, 32px);
          }
          .openings-member-detail-layout {
            display: grid;
            grid-template-columns:
              minmax(0, 1fr)
              clamp(320px, 38vw, 420px);
            grid-template-areas:
              "profile booking"
              "portfolio booking"
              "services booking";
            column-gap: var(--openings-booking-spacing-lg, 24px);
            row-gap: var(--openings-booking-spacing-lg, 24px);
            align-items: start;
          }
          .openings-member-detail-layout--no-services {
            grid-template-areas:
              "profile booking"
              "portfolio booking";
          }
          .openings-member-detail-layout--no-portfolio {
            grid-template-areas:
              "profile booking"
              "services booking";
          }
          .openings-member-detail-layout--profile-only {
            grid-template-areas: "profile booking";
          }
          .openings-member-detail-surface {
            box-sizing: border-box;
            width: 100%;
            min-width: 0;
            padding: var(--openings-booking-editorial-card-padding, 32px);
            border: 1px solid var(--openings-color-border, #e5e5e5);
            box-shadow: var(--openings-booking-surface-shadow, none);
            border-radius: var(--openings-booking-radius, 10px);
            background: var(--openings-color-surface, #fff);
          }
          .openings-member-detail-profile {
            grid-area: profile;
          }
          .openings-member-detail-profile-intro {
            display: flex;
            align-items: center;
            gap: var(--openings-booking-spacing-lg, 16px);
            min-width: 0;
          }
          .openings-member-detail-avatar {
            display: flex !important;
            width: 64px !important;
            height: 64px !important;
            flex: 0 0 64px;
            align-items: center;
            justify-content: center;
            overflow: hidden;
            border: 1px solid var(--openings-color-border, #e5e5e5);
            box-shadow: var(--openings-booking-surface-shadow, none);
            border-radius: 50%;
            background: var(--openings-color-background, #f2f2f2);
            color: var(--openings-color-text-secondary, #666);
            font-size: var(--openings-booking-font-size, 14px);
            font-weight: var(--openings-booking-font-weight-semibold, 600);
          }
          .openings-member-detail-avatar img {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .openings-member-detail-identity {
            min-width: 0;
          }
          .openings-member-detail-name {
            margin: 0;
            color: var(--openings-color-text, #111);
            font-family:
              var(--openings-booking-heading-font-family, inherit);
            font-size:
              var(--openings-booking-profile-title-font-size, 24px);
            font-weight: var(--openings-booking-font-weight-semibold, 600);
            line-height: 1.25;
            overflow-wrap: anywhere;
          }
          .openings-member-detail-contacts {
            display: flex;
            flex-wrap: wrap;
            gap: var(--openings-booking-spacing-xs, 4px)
              var(--openings-booking-spacing-md, 12px);
            margin-top: var(--openings-booking-spacing-xs, 4px);
          }
          .openings-member-detail-contact {
            color: var(--openings-color-text-secondary, #666);
            font-size: var(--openings-booking-font-size-sm, 13px);
            line-height: 1.5;
            text-decoration: none;
          }
          .openings-member-detail-contact:hover {
            color: var(--openings-color-text, #111);
            text-decoration: underline;
            text-underline-offset: 2px;
          }
          .openings-member-detail-bio {
            margin-top: var(--openings-booking-spacing-lg, 16px);
          }
          .openings-member-detail-bio p {
            margin: 0;
            color: var(--openings-color-text, #111);
            font-size: var(--openings-booking-font-size, 14px);
            line-height: 1.6;
            white-space: pre-line;
          }
          .openings-member-detail-bio p:not(.is-expanded) {
            display: -webkit-box;
            overflow: hidden;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 3;
          }
          .openings-member-detail-bio-toggle {
            margin-top: var(--openings-booking-spacing-sm, 8px);
            padding: 0;
            border: 0;
            background: transparent;
            color: var(--openings-color-text-secondary, #666);
            font: inherit;
            font-size: var(--openings-booking-font-size-sm, 13px);
            font-weight: var(--openings-booking-font-weight-medium, 500);
            cursor: pointer;
            text-decoration: underline;
            text-underline-offset: 2px;
          }
          .openings-member-detail-booking-rail {
            grid-area: booking;
            position: sticky;
            top: var(--openings-booking-spacing-lg, 24px);
            align-self: start;
            min-width: 0;
            overflow: visible;
          }
          .openings-member-detail-booking-intro {
            margin-bottom: var(--openings-booking-spacing-lg, 24px);
          }
          .openings-member-detail-booking-card
            .openings-services-booking-section {
            padding: 0;
          }
          .openings-member-detail-portfolio {
            grid-area: portfolio;
            display: grid;
            gap: var(--openings-booking-spacing-lg, 16px);
          }
          .openings-member-detail-section-title {
            margin: 0;
            color: var(--openings-color-text, #111);
          }
          .openings-member-detail-portfolio-media {
            overflow: hidden;
            border-radius: var(--openings-booking-radius-sm, 6px);
          }
          .openings-member-detail-layout
            .openings-member-detail-services {
            grid-area: services;
          }

          @media (max-width: 991px) {
            .openings-booking-wrapper.openings-member-detail-header-wrapper {
              padding-top:
                var(--openings-booking-editorial-outer-padding, 16px);
              padding-right:
                var(--openings-booking-editorial-outer-padding, 16px);
              padding-bottom: var(--openings-booking-spacing-sm, 8px);
              padding-left:
                var(--openings-booking-editorial-outer-padding, 16px);
            }
            .openings-booking-wrapper.openings-member-detail-body-wrapper {
              padding-top: var(--openings-booking-spacing-sm, 8px);
              padding-right:
                var(--openings-booking-editorial-outer-padding, 16px);
              padding-bottom:
                var(--openings-booking-editorial-outer-padding, 16px);
              padding-left:
                var(--openings-booking-editorial-outer-padding, 16px);
            }
            .openings-member-detail-layout,
            .openings-member-detail-layout--no-services,
            .openings-member-detail-layout--no-portfolio,
            .openings-member-detail-layout--profile-only {
              grid-template-columns: minmax(0, 1fr);
              column-gap: 0;
              row-gap: var(--openings-booking-spacing-lg, 24px);
            }
            .openings-member-detail-layout {
              grid-template-areas:
                "profile"
                "booking"
                "portfolio"
                "services";
            }
            .openings-member-detail-layout--no-services {
              grid-template-areas:
                "profile"
                "booking"
                "portfolio";
            }
            .openings-member-detail-layout--no-portfolio {
              grid-template-areas:
                "profile"
                "booking"
                "services";
            }
            .openings-member-detail-layout--profile-only {
              grid-template-areas:
                "profile"
                "booking";
            }
            .openings-member-detail-booking-rail {
              position: static;
              overflow: visible;
            }
          }

          @media (max-width: 767px) {
            .openings-member-detail-surface {
              padding: var(--openings-booking-editorial-card-padding, 16px);
            }
            .openings-member-detail-avatar {
              width: 48px !important;
              height: 48px !important;
              flex-basis: 48px;
            }
            .openings-member-detail-name {
              font-size:
                var(--openings-booking-profile-title-font-size, 20px);
            }
            .openings-member-detail-booking-card:has(
                .openings-member-inline-appointment
              )
              .openings-member-detail-booking-intro {
              position: absolute;
              width: 1px;
              height: 1px;
              padding: 0;
              margin: -1px;
              overflow: hidden;
              clip: rect(0, 0, 0, 0);
              white-space: nowrap;
              border: 0;
            }
          }
        `}</style>

        <BookingPageWrapper className="openings-member-detail-header-wrapper">
          <BookingPageHeaderWithDrawer
            salonName={salon.name}
            salonLogoUrl={salon.logoUrl}
            homeHref={homeHref}
            infoPanelProps={{
              schedules: salon.schedules,
              phoneNumber: salon.phoneNumber,
              supportEmail: salon.supportEmail,
              instagramHandle: salon.instagramHandle,
              blog: salon.blog,
            }}
          />
        </BookingPageWrapper>

        <BookingPageWrapper className="openings-member-detail-body-wrapper">
          <main
            className={`openings-member-detail-layout${layoutVariant}`}
            data-member-detail-layout
          >
            <section
              className="openings-member-detail-surface openings-member-detail-profile"
              data-member-profile
              aria-labelledby="openings-member-detail-name"
            >
              <div className="openings-member-detail-profile-intro">
                <Avatar
                  className="openings-member-detail-avatar"
                  src={member.photo || undefined}
                  alt={`${member.name} portrait`}
                >
                  {getInitials(member.name)}
                </Avatar>
                <div className="openings-member-detail-identity">
                  <h1
                    id="openings-member-detail-name"
                    className="openings-member-detail-name"
                  >
                    {member.name}
                  </h1>
                  {instagram || member.phone ? (
                    <div className="openings-member-detail-contacts">
                      {instagram ? (
                        <a
                          className="openings-member-detail-contact"
                          href={`https://instagram.com/${instagram}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          @{instagram}
                        </a>
                      ) : null}
                      {member.phone ? (
                        <a
                          className="openings-member-detail-contact"
                          href={`tel:${member.phone}`}
                        >
                          {formatPhoneNumberForDisplay(member.phone)}
                        </a>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>

              {member.bio ? (
                <div className="openings-member-detail-bio">
                  <p
                    ref={bioRef}
                    className={showFullBio ? "is-expanded" : undefined}
                  >
                    {showFullBio
                      ? member.bio
                      : member.bio.replace(/\s+/g, " ").trim()}
                  </p>
                  {bioCanExpand || showFullBio ? (
                    <button
                      type="button"
                      className="openings-member-detail-bio-toggle"
                      aria-expanded={showFullBio}
                      onClick={() => setShowFullBio((expanded) => !expanded)}
                    >
                      {showFullBio ? "Show less" : "Read more"}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </section>

            <section
              className="openings-member-detail-booking-rail"
              data-booking-rail
              aria-labelledby="openings-member-booking-title"
            >
              <div
                className="openings-member-detail-surface openings-member-detail-booking-card"
                data-booking-card
              >
                <div className="openings-member-detail-booking-intro">
                  <h2
                    id="openings-member-booking-title"
                    style={standaloneCardTitleStyle}
                  >
                    Book an appointment
                  </h2>
                </div>
                {bookingData ? (
                  <BookingProvider bookingData={bookingData}>
                    <MemberBookingSection />
                  </BookingProvider>
                ) : (
                  <p
                    style={{
                      margin: 0,
                      color:
                        "var(--openings-color-text-secondary, #666)",
                      fontSize:
                        "var(--openings-booking-font-size-sm, 13px)",
                      lineHeight: 1.5,
                    }}
                  >
                    Online booking is not available for this member yet.
                  </p>
                )}
              </div>
            </section>

            {hasPortfolio ? (
              <section
                className="openings-member-detail-surface openings-member-detail-portfolio"
                data-member-portfolio
                aria-labelledby="openings-member-portfolio-title"
              >
                <h2
                  id="openings-member-portfolio-title"
                  className="openings-member-detail-section-title"
                  style={standaloneCardTitleStyle}
                >
                  Portfolio
                </h2>
                <div className="openings-member-detail-portfolio-media">
                  <MemberPhotoCarousel
                    salonId={salon.id}
                    photos={portfolioItems}
                    fullBleed={false}
                    height={240}
                  />
                </div>
              </section>
            ) : null}

            {hasServices ? (
              <EditorialServiceLedger
                className="openings-member-detail-services"
                contentId="openings-member-services-ledger-content"
                groups={serviceGroups}
              />
            ) : null}
          </main>
        </BookingPageWrapper>
      </div>
    </EditorialBookingPageRoot>
  );
}
